"use client";

import React from "react";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons";

import useShareLink from "@/hooks/useShareLink";
import { useBreedData } from "@/domain";

import Container from "@/components/Container";
import Breadcrumb from "@/components/shared/Breadcrumb";
import Details from "@/components/shared/Details";
import Action from "@/components/shared/Action";
import Snackbar from "@/components/shared/Snackbar";
import Gallery from "@/components/shared/Gallery";
import ButtonGoToTop from "@/components/shared/ButtonGoToTop";

import AccordionsSection from "./AccordionsSection";
import PhyschicalCharacteristic from "./PhysicalCharacteristic";
import TecnicalSpecifications from "./TechnicalSpecifications";
import PuppiesSection from "./PuppiesSection";
import Programs from "./Programs";
import CobasiCuidaBreed from "./CobasiCuida";
import Faq from "./Faq";
import Articles from "./Articles";
import RelatedBreeds from "./RelatedBreeds";

import type { BreedProps } from "./breed.types";

import s from "./breed.module.scss";

const Breed = ({
  breed,
  isSignedIn,
  relatedBreeds,
  articles,
  params
}: BreedProps) => {
  const { snackbarData, handleShareLink, handleCloseSnackbar } = useShareLink();
  const {
    name,
    description,
    gallery,
    tecnicalSpecifications,
    initialAccordions,
    restAccordions,
    caracteristicasFisicas,
    imagemCaracteristicasFisicas,
    filhoteImage,
    filhoteDescription,
    faq,
    hasGallery,
    breadcrumb
  } = useBreedData(breed, params);

  return (
    <div className={s.box}>
      <Container>
        <div className={s.wrapper}>
          <Breadcrumb items={breadcrumb} />
          <div className={s.box_text_and_actions}>
            <Details
              title={name}
              description={description}
              maxLength={190}
              classNameLink="link-turquoise"
              isExpandable
            />
            <Action
              label="Compartilhar"
              className="link-turquoise"
              wrapperClassName={s.custom_actions_wrapper}
              icon={faShareNodes}
              onClick={() => handleShareLink({ title: name })}
            />
          </div>
          {hasGallery && (
            <Gallery
              images={gallery}
              label={name}
              variantButton="btn-turquoise"
              variantTabs="tabs-turquoise"
              variantDots="turquoise"
            />
          )}
          <div className={s.grid_container}>
            <div className={s.content}>
              {initialAccordions.length > 0 && (
                <AccordionsSection accordions={initialAccordions} />
              )}
              <PhyschicalCharacteristic
                caracteristicasFisicas={caracteristicasFisicas}
                imagemCaracteristicasFisicas={imagemCaracteristicasFisicas}
              />
              {restAccordions.length > 0 && (
                <AccordionsSection accordions={restAccordions} />
              )}
              <PuppiesSection
                name={name}
                filhoteImage={filhoteImage}
                filhoteDescription={filhoteDescription}
              />
              <Faq faq={faq} />
              {relatedBreeds.length > 0 && (
                <RelatedBreeds relatedBreeds={relatedBreeds} />
              )}
            </div>
            <div className={s.technical_specifications}>
              <TecnicalSpecifications
                tecnicalSpecifications={tecnicalSpecifications}
              />
              <Programs isSignedIn={isSignedIn} />
              <div className={s.actived_programs_lg}>
                <CobasiCuidaBreed params={params} />
              </div>
            </div>
          </div>
        </div>
      </Container>
      <ButtonGoToTop size="md" variant="turquoise" iconRounded />
      <Container>
        <div className={s.content_footer}>
          {articles.length > 0 && <Articles articles={articles} name={name} />}
          <div className={s.actived_programs_sm}>
            <CobasiCuidaBreed params={params} />
          </div>
        </div>
      </Container>
      <Snackbar
        theme="dark"
        position="right"
        onCloseButton={handleCloseSnackbar}
        {...snackbarData}
      />
    </div>
  );
};

export default Breed;
